import React, { useState, useCallback } from 'react';
import { Input, Button, Spin, Checkbox, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useProcessingFeeForm } from './hooks/useProcessingFeeForm';
import { useModulePermission } from '../../hooks/usePermission';
import SharedActionBar from '../../components/SharedActionBar/SharedActionBar';
import LookupField from '../../components/LookupField';

const { TextArea } = Input;

const P1112Form: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useModulePermission('pw1112f1');
  const {
    mode, isEditing, loading,
    records, current, currentIdx, refData,
    code, setCode,
    name, setName,
    remark, setRemark,
    processCode, setProcessCode,
    isOldCode, setIsOldCode,
    isDisabled, setIsDisabled,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    quickSearch,
  } = useProcessingFeeForm();

  const [searchText, setSearchText] = useState('');

  const handleQuickSearch = () => {
    if (searchText.trim()) {
      quickSearch(searchText.trim());
    }
  };

  const handleCopy = useCallback(() => {
    message.info('複製功能尚未實作');
  }, []);

  const handleExit = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  return (
    <Spin spinning={loading}>
      <div className="max-w-2xl mx-auto p-6 pb-20">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          工繳建檔
          <span className="ml-3 text-sm font-normal text-gray-500">
            {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search Bar (FoxPro Pwtqseek1) */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入工繳代號快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 300 }}
            maxLength={6}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mb-6">
          {/* Header: 工繳代號 + 建檔日期 + 建檔人員 */}
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">工繳代號</label>
              <Input
                value={code}
                onChange={e => setCode(e.target.value)}
                maxLength={6}
                disabled={mode !== 'add'}
                style={{ width: 200 }}
                placeholder="最多6字元"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">建檔日期</label>
              <Input value={current?.f112_29 || ''} disabled style={{ width: 140 }} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">建檔人員</label>
              <Input value={current?.f112_27 || ''} disabled style={{ width: 120 }} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">製程代號</label>
            <LookupField
              lookupType="processCode"
              value={processCode || ''}
              onChange={(c) => setProcessCode(c)}
              disabled={!isEditing}
              style={{ width: 200 }}
              title="搜尋製程"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">工繳名稱</label>
            <TextArea
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={100}
              disabled={!isEditing}
              rows={2}
              style={{ width: 400 }}
            />
          </div>

          {/* Shape1 Region: 加工說明 (F11U reference data — always visible) */}
          <div className="border border-gray-300 rounded p-3 bg-gray-50">
            <div className="grid grid-cols-2 gap-x-6 mb-2">
              <div className="text-sm font-semibold text-gray-600">加工說明－中文</div>
              <div className="text-sm font-semibold text-gray-600">加工說明－英文</div>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <Input value={refData?.f11u_111 || ''} disabled size="small" />
              <Input value={refData?.f11u_112 || ''} disabled size="small" />
              <Input value={refData?.f11u_121 || ''} disabled size="small" />
              <Input value={refData?.f11u_122 || ''} disabled size="small" />
              <Input value={refData?.f11u_132 || ''} disabled size="small" />
              <Input value={refData?.f11u_132 || ''} disabled size="small" />
              <Input value={refData?.f11u_141 || ''} disabled size="small" />
              <Input value={refData?.f11u_142 || ''} disabled size="small" />
              <Input value={refData?.f11u_151 || ''} disabled size="small" />
              <Input value={refData?.f11u_152 || ''} disabled size="small" />
              <Input value={refData?.f11u_161 || ''} disabled size="small" />
              <Input value={refData?.f11u_162 || ''} disabled size="small" />
              <Input value={refData?.f11u_171 || ''} disabled size="small" />
              <Input value={refData?.f11u_172 || ''} disabled size="small" />
              <Input value={refData?.f11u_181 || ''} disabled size="small" />
              <Input value={refData?.f11u_182 || ''} disabled size="small" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">說明</label>
            <TextArea
              value={remark}
              onChange={e => setRemark(e.target.value)}
              disabled={!isEditing}
              rows={3}
              style={{ width: 500 }}
            />
          </div>

          {/* Audit field: 最近異動日 (always visible) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">最近異動日</label>
            <Input value={current?.f112_28 || ''} disabled style={{ width: 140 }} />
          </div>

          <div className="flex items-center gap-6">
            <Checkbox
              checked={isDisabled}
              onChange={e => setIsDisabled(e.target.checked)}
              disabled={!isEditing}
            >
              此道加工不改變產品代號
            </Checkbox>
            <Checkbox
              checked={isOldCode}
              onChange={e => setIsOldCode(e.target.checked)}
              disabled={!isEditing}
            >
              舊代號
            </Checkbox>
          </div>
        </div>
      </div>

      {/* SharedActionBar — FoxPro Pwtcmd1: CMDSRCH + CMDADD + CMDMODI + Command1(複製), NO CMDDEL */}
      <SharedActionBar
        mode={mode}
        isEditing={isEditing}
        hasRecord={!!current}
        currentIdx={currentIdx}
        recordCount={records.length}
        onNavFirst={navFirst}
        onNavPrev={navPrev}
        onNavNext={navNext}
        onNavLast={navLast}
        onAdd={startAdd}
        onSave={save}
        onModify={startEdit}
        onCancel={cancel}
        onCopy={handleCopy}
        onExit={handleExit}
        canView={permissions.canView}
        canCreate={permissions.canCreate}
        canEdit={permissions.canEdit}
        canDelete={false}
      />
    </Spin>
  );
};

export default P1112Form;
